'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';

import {
  DRAWER_EVENT_TYPES,
  DrawerEventData,
  track,
  trackElementPresented,
} from '@/utils/rudderstack';

type DrawerProps = {
  open: boolean;
  onOpen?: () => void;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  widthClassName?: string;
  eventData?: DrawerEventData;
  customClassNames?: string;
};

export default function Drawer({
  open,
  onOpen,
  onClose,
  title = '',
  children,
  widthClassName,
  eventData,
  customClassNames,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const elementPresentedTrackedRef = useRef(false);

  const handleOpen = useCallback(() => {
    if (eventData?.content && eventData?.contentType) {
      track(DRAWER_EVENT_TYPES.DRAWER_VIEWED, {
        url: window.location.href,
        content: eventData.content,
        contentType: eventData.contentType,
      });
    }

    // Only track Element Presented once per drawer session
    if (eventData?.elementPresented && !elementPresentedTrackedRef.current) {
      trackElementPresented(eventData.elementPresented);
      elementPresentedTrackedRef.current = true;
    }

    onOpen?.();
  }, [eventData, onOpen]);

  const handleClose = () => {
    if (eventData?.content && eventData?.contentType) {
      track(DRAWER_EVENT_TYPES.DRAWER_CLOSED, {
        url: window.location.href,
        content: eventData.content,
        contentType: eventData.contentType,
      });
    }

    // Reset the tracking flag when drawer closes
    elementPresentedTrackedRef.current = false;

    onClose();
  };

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (open) {
      const prevOverflow = document.body.style.overflow;

      document.body.style.overflow = 'hidden';

      const id = requestAnimationFrame(() => panelRef.current?.focus());

      handleOpen();

      return () => {
        cancelAnimationFrame(id);

        document.body.style.overflow = prevOverflow;
      };
    }
  }, [open, handleOpen]);

  return (
    <>
      <div
        className={classNames(
          'fixed inset-0 z-[99999] bg-black/65 transition-opacity duration-300',
          {
            'opacity-100': open,
            'opacity-0 pointer-events-none': !open,
          },
        )}
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
        tabIndex={open ? 0 : -1}
        ref={panelRef}
        className={classNames(
          'fixed right-0 top-0 z-[99999] h-dvh bg-white shadow-2xl',
          'transition-transform duration-300 will-change-transform',
          'flex flex-col',
          widthClassName || 'md:w-[min(90vw,640px)] w-full',
          {
            'translate-x-0': open,
            'translate-x-full': !open,
          },
        )}
      >
        <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-grey-mid-light px-6 md:px-8 py-4 md:py-6">
          <Heading
            text={title}
            align="left"
            tag={HeadingTag.H3}
            size={HeadingSize.H3}
            data-testid="account-page-heading"
          />

          <button
            className="flex flex-1 justify-end"
            onClick={handleClose}
            aria-label="Close drawer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-8 w-8 text-grey-dark"
            >
              <path
                fillRule="evenodd"
                d="M6.225 4.811a.75.75 0 011.06 0L12 9.525l4.715-4.714a.75.75 0 111.06 1.06L13.06 10.586l4.715 4.714a.75.75 0 11-1.06 1.06L12 11.646l-4.715 4.714a.75.75 0 11-1.06-1.06l4.714-4.715-4.714-4.714a.75.75 0 010-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div
          className={classNames(
            'min-h-0 flex-1 overflow-y-auto p-4',
            customClassNames,
          )}
        >
          {children}
        </div>
      </div>
    </>
  );
}
